import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { environment } from 'environments/environment';
import {
  BehaviorSubject,
  Observable,
  catchError,
  defer,
  distinctUntilChanged,
  finalize,
  map,
  of,
  share,
  tap,
} from 'rxjs';

import {
  Landing,
  LandingTarget,
  MeResponse,
  OwnerInfo,
  OwnershipTransferResult,
  PermissionKey,
  Seats,
} from '../../interfaces/Permissions';
import { ApiResponse } from '../../interfaces/httpResponses';
import { User } from '../../interfaces/User';
import { AccountService } from '../features/account.service';

/** Permissions only the owner has (never assignable). */
export const EXCLUSIVE_PERMISSIONS: readonly PermissionKey[] = [
  'company:manage',
  'users:manage',
  'roles:manage',
  'ownership:transfer',
];

/**
 * Legacy sessions (no `permissions` in the stored user) behave as before:
 * everything is allowed, except what used to be admin-only.
 */
const LEGACY_ADMIN_ONLY: readonly PermissionKey[] = [
  ...EXCLUSIVE_PERMISSIONS,
  'arca:configure',
  'promotions:view',
  'promotions:manage',
  'treasury:manage',
  'checks:manage',
  'payments:correct',
  'supplier-accounts:pay',
];

export interface AccessState {
  /** `null` means legacy session: no permissions came from the API. */
  permissions: ReadonlySet<PermissionKey> | null;
  roles: string[];
  isOwner: boolean;
  hash: string | null;
  landing: Landing | null;
}

const EMPTY_STATE: AccessState = {
  permissions: null,
  roles: [],
  isOwner: false,
  hash: null,
  landing: null,
};

/**
 * Single source of truth for "what can this person do". Fed by the login
 * payload (stored user) and refreshed from `GET /me`. Shared state lives in
 * BehaviorSubjects (no Signals in this project).
 */
import { blockingLoaderHeaders } from '../../interceptors/loader.interceptor';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly ME_URL = `${environment.apiUrl}user/account/me`;

  private readonly _state$ = new BehaviorSubject<AccessState>(EMPTY_STATE);
  public readonly state$ = this._state$.asObservable();

  private readonly _owner$ = new BehaviorSubject<OwnerInfo | null>(null);
  public readonly owner$ = this._owner$.asObservable();

  private readonly _seats$ = new BehaviorSubject<Seats | null>(null);
  public readonly seats$ = this._seats$.asObservable();

  private readonly _catalogVersion$ = new BehaviorSubject<string | null>(null);
  public readonly catalogVersion$ = this._catalogVersion$.asObservable();

  private inFlight: Observable<boolean> | null = null;

  constructor(
    private readonly injector: Injector,
    private readonly accountService: AccountService,
  ) {
    this.syncFromStorage();
    // Optional chaining keeps specs that stub AccountService working.
    this.accountService.sessionChanged$?.subscribe(() =>
      this.syncFromStorage(),
    );
  }

  /** Rebuilds the state from the stored user. */
  public syncFromStorage(): void {
    this._state$.next(this.buildState(this.accountService.userData));
    if (!this.accountService.userData?._id) {
      this._owner$.next(null);
      this._seats$.next(null);
      this._catalogVersion$.next(null);
    }
  }

  public get state(): AccessState {
    return this._state$.value;
  }

  public get isLegacy(): boolean {
    return this.state.permissions === null;
  }

  public get isOwner(): boolean {
    const state = this.state;
    return state.permissions === null
      ? this.accountService.isAdmin
      : state.isOwner;
  }

  public get owner(): OwnerInfo | null {
    return this._owner$.value;
  }

  public get seats(): Seats | null {
    return this._seats$.value;
  }

  public get roles(): string[] {
    return this.state.roles;
  }

  public get hash(): string | null {
    return this.state.hash;
  }

  public get landing(): Landing | null {
    return this.state.landing;
  }

  public get permissionList(): PermissionKey[] {
    return [...(this.state.permissions ?? [])];
  }

  /** True when the person has ALL the given permissions. */
  public can(keys: PermissionKey | readonly PermissionKey[]): boolean {
    const list = typeof keys === 'string' ? [keys] : keys;
    return list.every((key) => this.has(key));
  }

  /** True when the person has AT LEAST ONE of the given permissions. */
  public canAny(keys: readonly PermissionKey[]): boolean {
    return keys.length === 0 || keys.some((key) => this.has(key));
  }

  /** Reactive `can`, for templates and menu items. */
  public can$(
    keys: PermissionKey | readonly PermissionKey[],
  ): Observable<boolean> {
    return this.state$.pipe(
      map(() => this.can(keys)),
      distinctUntilChanged(),
    );
  }

  /**
   * First route segment the person lands on after login (and on the root
   * URL): always "Inicio". The dashboard stays one click away in the menu for
   * whoever has `dashboard:view`; `landing.target` of `/me` is ignored.
   */
  public get landingRoute(): LandingTarget {
    return 'home';
  }

  /** Absolute URL of `landingRoute`. */
  public get landingUrl(): string {
    return `/${this.landingRoute}`;
  }

  /** True when a v2 session has no permission at all. */
  public get hasNoAccess(): boolean {
    const permissions = this.state.permissions;
    return permissions !== null && permissions.size === 0;
  }

  /**
   * Re-reads `/me`. Never throws: a failed refresh keeps the current state.
   * Calls made while a request is in flight share it.
   */
  public refresh(): Observable<boolean> {
    return defer(() => {
      if (!this.inFlight) {
        // Resolved lazily so screens that only call `can()` do not need HttpClient.
        const http = this.injector.get(HttpClient);
        this.inFlight = http
          .get<ApiResponse<MeResponse>>(this.ME_URL, {
            headers: blockingLoaderHeaders({
              message: 'Cargando tu cuenta...',
            }),
          })
          .pipe(
            tap((response) => this.applyMe(response.payload)),
            map(() => true),
            catchError(() => of(false)),
            finalize(() => (this.inFlight = null)),
            share(),
          );
      }
      return this.inFlight;
    });
  }

  /** Fire-and-forget refresh. */
  public refreshNow(): void {
    this.refresh().subscribe();
  }

  /** Applies the `you` block of a confirmed ownership transfer. */
  public applyOwnershipTransfer(you: OwnershipTransferResult['you']): void {
    const user = this.accountService.userData;
    if (user?._id) {
      this.accountService.setUserData({
        ...user,
        isOwner: you.isOwner,
        roles: you.roles,
        permissions: you.permissions,
        permissionsHash: you.permissionsHash,
      });
    }
  }

  public applyMe(me: MeResponse): void {
    const stored = this.accountService.userData;
    const incoming = me.user;
    const merged: User = {
      ...stored,
      name: incoming.name ?? stored.name,
      email: incoming.email ?? stored.email,
      isPlatformAdmin: incoming.isPlatformAdmin ?? stored.isPlatformAdmin,
      isOwner: incoming.isOwner,
      roles: incoming.roles,
      permissions: incoming.permissions,
      permissionsHash: incoming.permissionsHash,
      landing: incoming.landing,
    };
    if (incoming.role) merged.role = incoming.role as User['role'];

    // Triggers syncFromStorage through sessionChanged$.
    this.accountService.setUserData(merged);
    this._owner$.next(me.owner ?? null);
    this._seats$.next(me.seats ?? null);
    this._catalogVersion$.next(me.catalogVersion ?? null);
  }

  public setSeats(seats: Seats | null): void {
    this._seats$.next(seats);
  }

  private has(key: PermissionKey): boolean {
    const permissions = this.state.permissions;
    if (permissions === null) {
      return LEGACY_ADMIN_ONLY.includes(key)
        ? this.accountService.isAdmin
        : true;
    }
    return permissions.has(key);
  }

  private buildState(user: User | undefined): AccessState {
    if (!user?._id || !Array.isArray(user.permissions)) {
      return { ...EMPTY_STATE, landing: user?.landing ?? null };
    }
    return {
      permissions: new Set(user.permissions),
      roles: user.roles ?? [],
      isOwner: user.isOwner === true,
      hash: user.permissionsHash ?? null,
      landing: user.landing ?? null,
    };
  }
}
